import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/apiService";

/**
 * AuditResults — Per-question-set audit.
 *
 * Shows: (1) Map each Q to a CO, (2) Bloom's distribution, (3) CO coverage for the set.
 * Route: /audit-results/qs/:qsId
 */

function AuditResults() {
  const { qsId, id } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiAuditRun, setAiAuditRun] = useState(null);

  useEffect(() => {
    async function runAuditDirect() {
      setLoading(true);
      setError(null);
      try {
        const questionSetId = qsId || id;

        // Fetch question set, lesson, course, outcomes for context
        const qs = await apiService.getQuestionSetById(questionSetId);

        const lesson = await apiService.getLessonById(qs.lesson);

        const course = await apiService.getCourseById(lesson.course);

        let outcomes = [];
        try {
          outcomes = await apiService.getOutcomesByCourse(lesson.course) || [];
        } catch (e) {}

        // Run AI audit directly — no keyword step
        const auditData = await apiService.runAudit(questionSetId);
        setAiAuditRun(auditData);

        const mappings = (auditData.mappings || []).map(m => ({
          questionId: m.question,
          questionText: m.question_text || '',
          mappedCo: m.mapped_co_name || 'Unmapped',
          coDescription: m.co_description || '',
          blooms: m.blooms || 'N/A',
          difficulty: m.difficulty || 'N/A',
          confidence: m.confidence || 'None',
        }));

        const coveredCOs = new Set(mappings.filter(m => m.mappedCo !== 'Unmapped').map(m => m.mappedCo));
        const uncoveredCOs = outcomes.filter(o => !coveredCOs.has(o.short_name));

        setResults({
          questionSet: qs,
          course: course.title,
          lesson: lesson.title,
          totalQuestions: auditData.total_questions || mappings.length,
          totalOutcomes: outcomes.length,
          coveredCount: auditData.covered_count || coveredCOs.size,
          coverageRate: auditData.coverage_rate || (outcomes.length > 0 ? Math.round((coveredCOs.size / outcomes.length) * 100) : 0),
          blooms: auditData.blooms_distribution || { remember: 0, understand: 0, apply: 0, analyze: 0, evaluate: 0, create: 0 },
          qToCoMap: mappings,
          outcomes,
          uncoveredCOs,
          auditMethod: 'ai',
          run_at: new Date().toLocaleString(),
        });
      } catch (err) {
        console.error('Audit error:', err);
        setError('Failed to run audit: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    runAuditDirect();
  }, [qsId, id]);

  const runAiAudit = async () => {
    const questionSetId = qsId || id;
    setAiRunning(true);
    try {
      const data = await apiService.runAudit(questionSetId);
      setAiAuditRun(data);
      // Apr 29 fix (bug #8): show a clear "done" toast so the user knows the
      // re-run finished. Old behaviour: button just stopped saying "Running...".
      const totalQs = (data.mappings || []).length;
      // Backend stores coverage_rate already as a percent integer (e.g. 75
      // for 75%), so no extra *100 multiply.
      const cov = data.coverage_rate != null
        ? `${data.coverage_rate}% CO coverage`
        : 'audit complete';
      // Set a transient toast via window-scoped event the page listens to.
      window.setTimeout(() => {
        try {
          alert(`AI audit complete: ${totalQs} question(s) re-mapped, ${cov}.`);
        } catch (_) {}
      }, 50);
      // Update results with AI mappings
      const mappings = (data.mappings || []).map(m => ({
        questionId: m.question,
        questionText: m.question_text || '',
        mappedCo: m.mapped_co_name || 'Unmapped',
        coDescription: m.co_description || '',
        blooms: m.blooms || 'N/A',
        difficulty: m.difficulty || 'N/A',
        confidence: m.confidence || 'None',
      }));
      if (mappings.length > 0) {
        setResults(prev => {
          const coveredCOs = new Set(mappings.filter(m => m.mappedCo !== 'Unmapped').map(m => m.mappedCo));
          const uncoveredCOs = (prev.outcomes || []).filter(o => !coveredCOs.has(o.short_name));
          return {
            ...prev,
            qToCoMap: mappings,
            coverageRate: data.coverage_rate,
            coveredCount: data.covered_count,
            blooms: data.blooms_distribution || prev.blooms,
            auditMethod: data.audit_method || 'ai',
            uncoveredCOs,
          };
        });
      }
    } catch (err) {
      setError('AI audit failed: ' + err.message);
    } finally {
      setAiRunning(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Running AI audit... This may take 30-60 seconds.</div>;
  if (error) return <div style={{ padding: '20px', color: '#dc3545' }}>{error}</div>;
  if (!results) return <div>No results.</div>;

  return (
    <div>
      <button className="btn btn-secondary" onClick={() => navigate(-1)}>&larr; Back</button>

      <div style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
        <h1>Question Set Audit</h1>
        <p style={{ color: 'var(--text-light)', marginTop: '4px' }}>
          {results.questionSet.name || 'Question Set'} | {results.course} | {results.lesson} | {results.totalQuestions} questions | {results.run_at}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#042F54' }}>{results.totalQuestions}</div>
          <div style={{ fontSize: '13px', color: '#888' }}>Questions in Set</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: results.coverageRate < 50 ? '#dc3545' : '#042F54' }}>
            {results.coverageRate}%
          </div>
          <div style={{ fontSize: '13px', color: '#888' }}>CO Coverage ({results.coveredCount}/{results.totalOutcomes})</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#042F54' }}>{results.uncoveredCOs.length}</div>
          <div style={{ fontSize: '13px', color: '#888' }}>COs Not Covered</div>
        </div>
      </div>

      {/* Bloom's distribution */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ marginBottom: '12px' }}>Bloom's Taxonomy Distribution</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {Object.entries(results.blooms).map(([level, count]) => (
            <div key={level} style={{ textAlign: 'center', minWidth: '80px' }}>
              <div style={{
                height: `${Math.max(count * 8, 20)}px`, width: '60px',
                background: count > 8 ? '#4a90e2' : count > 4 ? '#28a745' : '#f5a623',
                borderRadius: '4px 4px 0 0', margin: '0 auto'
              }} />
              <div style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>{count}</div>
              <div style={{ fontSize: '11px', color: '#888', textTransform: 'capitalize' }}>{level}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Question → CO Mapping table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
        <h3 style={{ marginBottom: '16px' }}>Question → Course Outcome Mapping</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ padding: '10px 12px', textAlign: 'left', background: '#042F54', color: '#fff', width: '40px' }}>#</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', background: '#042F54', color: '#fff' }}>Question</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', background: '#042F54', color: '#fff', width: '70px' }}>CO</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', background: '#042F54', color: '#fff', width: '80px' }}>Bloom's</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', background: '#042F54', color: '#fff', width: '70px' }}>Diff.</th>
            </tr>
          </thead>
          <tbody>
            {results.qToCoMap.map((m, i) => (
              <tr key={i} style={{ background: m.mappedCo === 'Unmapped' ? '#fff3cd' : i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>{i + 1}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>{m.questionText.substring(0, 120)}{m.questionText.length > 120 ? '...' : ''}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: m.mappedCo === 'Unmapped' ? '#dc3545' : '#042F54' }}>{m.mappedCo}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', textTransform: 'capitalize' }}>{m.blooms}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', textTransform: 'capitalize' }}>{m.difficulty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Uncovered COs */}
      {results.uncoveredCOs.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', color: '#dc3545' }}>Gaps — COs Not Covered by This Set</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', background: '#dc3545', color: '#fff', width: '80px' }}>CO</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', background: '#dc3545', color: '#fff' }}>Description</th>
              </tr>
            </thead>
            <tbody>
              {results.uncoveredCOs.map((o, i) => (
                <tr key={o.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>{o.short_name}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>{o.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>Back to Question Set</button>
        <button
          className="btn"
          style={{ background: '#28a745', color: '#fff' }}
          onClick={runAiAudit}
          disabled={aiRunning}
        >
          {aiRunning ? 'Running AI Audit...' : results.auditMethod === 'ai' ? 'Re-run AI Audit' : 'Run AI Audit'}
        </button>
        <button className="btn" style={{ background: '#042F54', color: '#fff' }} onClick={() => {
          const json = JSON.stringify(results, null, 2);
          const blob = new Blob([json], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `audit_${results.questionSet.name || 'qset'}.json`;
          a.click();
          URL.revokeObjectURL(url);
        }}>Download JSON</button>
        {aiAuditRun && (
          <button className="btn" style={{ background: '#4a90e2', color: '#fff' }} onClick={async () => {
            try {
              const res = await apiService.downloadAuditCsv(aiAuditRun.id);
              const url = URL.createObjectURL(res.data);
              const a = document.createElement('a');
              a.href = url;
              a.download = `audit_qs${aiAuditRun.question_set}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) { alert('CSV download failed: ' + e.message); }
          }}>Download CSV</button>
        )}
        {aiAuditRun && (
          <button className="btn" style={{ background: '#F56A3F', color: '#fff' }} onClick={async () => {
            try {
              const res = await apiService.downloadAuditPdf(aiAuditRun.id);
              const url = URL.createObjectURL(res.data);
              const a = document.createElement('a');
              a.href = url;
              a.download = `audit_qs${aiAuditRun.question_set}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) { alert('PDF download failed: ' + e.message); }
          }}>Download PDF</button>
        )}
        <button className="btn" style={{ background: '#888', color: '#fff' }} onClick={() => window.print()}>Print</button>
      </div>
      {results.auditMethod && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
          Audit method: {results.auditMethod === 'ai' ? 'AI-powered (Azure OpenAI)' : 'Keyword matching (local)'}
        </div>
      )}
    </div>
  );
}

export default AuditResults;
